import { computed, inject, Injectable, resource } from '@angular/core';
import { AccountService } from '@core/auth';
import { TuyauService } from '@core/tuyau';

/**
 * How many incident tasks are the employee's own — the navigation badge, and
 * nothing else.
 *
 * A dedicated endpoint rather than the list's `metadata.total`: the badge is on
 * every screen of the portal, including the ones that never load a task list,
 * and the list's own request is a page of enriched rows (PRD, Implementation
 * Decisions → Reads).
 *
 * It reads nothing until the account says the area is this employee's, so an
 * employee outside the beta never fires a request the gate would refuse.
 */
@Injectable({ providedIn: 'root' })
export class IncidentTaskCountService {
  private tuyau = inject(TuyauService);
  private account = inject(AccountService);

  private countResource = resource({
    params: () => ({ allowed: this.account.canAccessIncidents() }),
    defaultValue: 0,
    loader: async ({ params }) => {
      if (!params.allowed) return 0;

      const response =
        await this.tuyau.client.api.employeeV2.incidents.myTasks.count({});

      return response.data.count;
    },
  });

  /**
   * `resource.value()` *throws* in the error state rather than answering the
   * default, and `NavigationService` reads this inside a computed — so a count
   * that 403s would take down the whole navigation rail, on every screen,
   * instead of leaving the badge at zero. `hasValue()` is false on error and
   * never touches `value()`.
   */
  readonly count = computed(() =>
    this.countResource.hasValue() ? this.countResource.value() : 0
  );

  readonly isLoading = this.countResource.isLoading;

  /**
   * Re-read after a write. Taking a task adds one, completing one removes it,
   * and both happen on the detail — which the reader may have opened from
   * either list, so both list hosts call this.
   */
  reload() {
    this.countResource.reload();
  }
}
